import React, { useState, useEffect, useCallback } from "react";
import {
  SafTextArea,
  SafButton,
  SafAlert,
} from "@saffron/core-components/react";
import { useParams } from "react-router-dom";

const getMiddlewareEndpoint = (operation) => {
  const baseEndpoint = process.env.REACT_APP_MIDDLEWARE_ENDPOINT;
  // console.log("Endpoint:", baseEndpoint);
  if (!baseEndpoint) {
    console.error("Middleware endpoint is not defined.");
    return "";
  }
  return `${baseEndpoint}&operation=${operation}`;
};

const Custom = ({ activeTabName = "Unknown Tab" }) => {
  const { assetId } = useParams();
  const storedUserData = localStorage.getItem("userData");
  // const userData = storedUserData
  //   ? JSON.parse(storedUserData)
  //   : { id: "C299979", fullName: "K Sai Jateen Dora", firstName: "K Sai Jateen", lastName: "Dora" };

  const userData = JSON.parse(storedUserData);

  const [formData, setFormData] = useState({
    comments: "",
  });
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [alertMessage, setAlertMessage] = useState("");
  const [customData, setCustomData] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const getCustomData = useCallback(async () => {
    if (!assetId) return;

    const operation = "get";
    const endpoint = getMiddlewareEndpoint(operation);

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
    const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!response.ok) {
        console.error(`Error: ${response.status} ${response.statusText}`);
        return;
      }

      const data = await response.json();

      // Filter comments by the current active tab
      if (Array.isArray(data)) {
        const allComments = data.map((item) => item.comments || []).flat();
        const tabFilteredComments = allComments.filter(
          (comment) => comment?.tab === activeTabName
        );
        setCustomData(tabFilteredComments);
      } else if (data && data.comments) {
        const tabFilteredComments = data.comments.filter(
          (comment) => comment?.tab === activeTabName
        );
        setCustomData(tabFilteredComments);
      } else {
        setCustomData([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setCustomData([]);
    }
  }, [assetId, activeTabName]);

  // Refetch data when assetId or activeTabName changes
  useEffect(() => {
    getCustomData();
  }, [assetId, activeTabName, getCustomData]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (formData.comments.trim() === "") {
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Comments field cannot be empty");
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
      return;
    }

    const comment = {
      user: userData.fullName,
      comment: formData.comments,
      timestamp: new Date().toISOString().split(".")[0],
      tab: activeTabName,
    };

    try {
      // First, check if architecture-details exists for this asset
      const operation = "get";
      const getEndpoint = getMiddlewareEndpoint(operation);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Failed to check existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      const existingDataResponse = await getResponse.json();
      console.log("Existing data response:", existingDataResponse);

      let documentData;

      // Handle different response formats
      if (
        Array.isArray(existingDataResponse) &&
        existingDataResponse.length > 0
      ) {
        // If the API returns an array
        documentData = existingDataResponse[0];
        if (!documentData.comments) {
          documentData.comments = [];
        }
        documentData.comments.push(comment);
      } else if (
        existingDataResponse &&
        typeof existingDataResponse === "object" &&
        existingDataResponse.id === "architecture-details"
      ) {
        // If it's already a single object
        documentData = existingDataResponse;
        if (!documentData.comments) {
          documentData.comments = [];
        }
        documentData.comments.push(comment);
      } else {
        // If architecture-details doesn't exist, create it
        documentData = {
          id: "architecture-details",
          asset_id: assetId,
          arch_diags: [],
          adrs: [],
          road_map: [],
          integrations: [],
          actors: [],
          comments: [comment],
        };
      }

      // Now insert/update the document
      await insertDataToDB(dbName, containerName, documentData, assetId);

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Comment was successfully added");
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
      setFormData({
        comments: "",
      });
      getCustomData(); // Refresh data after insertion
    } catch (err) {
      console.error("Error saving comment:", err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to add comment: " + err.message);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
    }
  };

  const insertDataToDB = async (dbName, containerName, data, partitionKey) => {
    const operation = "insert";
    const endpoint = getMiddlewareEndpoint(operation);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: data,
          partitionKey: partitionKey,
        }),
      });

      if (!response.ok) {
        const errorDetail = await response.text();
        throw new Error(
          `Error: ${response.status} ${response.statusText}. Details: ${errorDetail}`
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error inserting data:", error.message);
      throw error;
    }
  };

  const handleOnClose = () => {
    setOpenAlertOptions(undefined);
  };

  return (
    <div className="headers">
      {openAlertOptions && (
        <SafAlert
          appearance={openAlertOptions[1]}
          alertType={openAlertOptions[0]}
          iconAriaLabel="icon aria label"
          closeAriaLabel="Close"
          onClose={handleOnClose}
        >
          {alertMessage}
        </SafAlert>
      )}

      <div style={{ width: "1000px" }}>
        <SafTextArea
          label="Comments"
          id="text-area"
          name="comments"
          value={formData.comments}
          onChange={handleInputChange}
          validationMessage="Custom validation message"
          optionalText=""
          data-testid="default-textarea"
        />
        <br />
        <SafButton appearance="primary" size="small" onClick={handleSubmitForm}>
          Submit
        </SafButton>
      </div>
      <div style={{ maxWidth: "1000px", margin: "0" }}>
        <div style={{ marginTop: "20px" }}>
          {customData.length > 0 ? (
            customData
              .slice()
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .map((comment, commentIndex) => (
                <div
                  key={commentIndex}
                  style={{
                    border: "1px solid #ccc",
                    padding: "10px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  {comment?.comment ? (
                    comment.comment.split("\n").map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))
                  ) : (
                    <span>No comment available</span>
                  )}
                  <div>
                    <small>{comment?.user}&nbsp;&nbsp;&nbsp;</small>
                    <small>
                      {new Date(comment?.timestamp).toLocaleString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        timeZoneName: "short",
                      })}
                    </small>
                  </div>
                </div>
              ))
          ) : (
            <div
              style={{
                padding: "10px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              No comments found for this tab
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Custom;
